import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useProducts } from "@/context/ProductsContext";

export const VARIANTS = {
  "50ml": { label: "50 ml Parfum", priceKey: "price50" },
  "10ml": { label: "10 ml Roll-On", priceKey: "price10" },
  combo: { label: "50ml + 10ml Combo", priceKey: "priceCombo" },
};

const CartContext = createContext(null);
const STORAGE_KEY = "r30_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const { products } = useProducts();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((slug, variant) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.slug === slug && i.variant === variant);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(10, next[idx].qty + 1) };
        return next;
      }
      return [...prev, { slug, variant, qty: 1 }];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((slug, variant, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => !(i.slug === slug && i.variant === variant))
        : prev.map((i) => (i.slug === slug && i.variant === variant ? { ...i, qty: Math.min(10, qty) } : i))
    );
  }, []);

  const removeItem = useCallback((slug, variant) => setItems((prev) => prev.filter((i) => !(i.slug === slug && i.variant === variant))), []);
  const clear = useCallback(() => setItems([]), []);

  const detailed = useMemo(
    () =>
      items.map((i) => {
        const p = products.find((pr) => pr.slug === i.slug);
        const v = VARIANTS[i.variant];
        const price = p?.[v.priceKey] ?? 0;
        return { ...i, name: p?.name || i.slug, image: p?.image, variantLabel: v.label, price, lineTotal: price * i.qty };
      }),
    [items, products]
  );

  const subtotal = detailed.reduce((s, i) => s + i.lineTotal, 0);
  const shipping = subtotal === 0 || subtotal >= 999 ? 0 : 79;
  const total = subtotal + shipping;
  const count = items.reduce((s, i) => s + i.qty, 0);

  const value = useMemo(
    () => ({ items, detailed, addItem, updateQty, removeItem, clear, subtotal, shipping, total, count, open, setOpen }),
    [items, detailed, addItem, updateQty, removeItem, clear, subtotal, shipping, total, count, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
